import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import StatusLegend from './StatusLegend'
import Calendar from '../common/Calendar'
import LoadingSpinner from '../common/LoadingSpinner'
import CycleDetailsModal from '../common/CycleDetailsModal'
import { toast } from '../common/Toast'
import SimpleButton from '../common/SimpleButton'
import { getProductCycles, getProductCycleDetails, syncBravoCyclesApi } from '../../utils/requests/productsAPI'
import { getCycleDetails, getMQTTCycles } from '../../utils/requests/trackingAPI'
import { convertToSimpleDateWithFullTime, formatDate, formatVal, months } from '../../utils/filters/date'
import { useWindowSize } from '../../libs/hooks'
import { useAppContext } from '../../libs/contextLib'
import './cycle-calendar.scss'

const CycleCalendar = (props) => {
  const { t, i18n } = useTranslation()
  const { showFeatureDev } = useAppContext()
  const windowSize = useWindowSize()[0]
  const lastCycleDate = props.lastCycleDate || window.history.state?.state?.lastCycleDate || window.history.state?.lastCycleDate
  const isG4Plus = window.history.state?.state?.is_g4_plus || props.isG4Plus
  const hasOnlineAccess = props.hasOnlineAccess || window.history.state?.state?.hasOnlineAccess || window.history.state?.hasOnlineAccess
  const [events, setEvents] = useState([])
  const [currentDate, setCurrentDate] = useState(null)
  const initialNavPendingRef = useRef(!!lastCycleDate)
  const [cycleDetails, setCycleDetails] = useState({})
  const [cycleDetailsDeviceData, setCycleDetailsDeviceData] = useState({})
  const [selectedDay, setSelectedDay] = useState(
    lastCycleDate ? `${formatDate(lastCycleDate)}` : `${new Date().getFullYear()}-${formatVal(new Date().getMonth() + 1)}-${formatVal(new Date().getDate())}`,
  )
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [showCycleDetails, setShowCycleDetails] = useState(false)
  const [showSpinner, setShowSpinner] = useState(false)
  const calendarRef = useRef(null)
  const calendarWrapperRef = useRef(null)
  const langCode = i18n.language ? i18n.language.substring(0, 2) : 'en'
  const localMonths = months()
  const eventDisp = 'block'
  const isBravoProduct = props.model?.toLowerCase().includes('bravo')

  const legendData = [
    {
      icon: <div className="success-icon status-icon"></div>,
      label: t('device_context.successful'),
    },
    {
      icon: <div className="error-icon status-icon"></div>,
      label: t('device_context.fault_cycle'),
    },
    {
      icon: <div className="warning-icon status-icon"></div>,
      label: t('device_context.interrupted_no_cf'),
    },
    // {
    //   icon: <div className="pending-icon status-icon"></div>,
    //   label: t("device_context.check_usb"),
    // },
  ]
  const isG4PlusAPIEnabled = process.env.REACT_APP_G4PLUS_API_TOGGLE === 'on'
  const extractTrigger = () => {
    const hs = window.history.state || {}
    const inner = hs && typeof hs === 'object' && hs.state && typeof hs.state === 'object' ? hs.state : hs
    const date = inner.cycleFaultDate || inner.cycle_date || inner.date
    const fault = inner.cycleFault ?? inner.cycle_fault
    const numRaw = inner.cycleNumber ?? inner.cycle_number
    const num = numRaw !== undefined && numRaw !== null && numRaw !== '' ? Number(numRaw) : null
    if (date || fault !== undefined || num !== null) return { cycleFaultDate: date || null, cycleFault: fault, cycleNumber: num }
    return null
  }

  const [pendingTrigger, setPendingTrigger] = useState(() => extractTrigger())
  const initialTargetDate = pendingTrigger?.cycleFaultDate || lastCycleDate || null

  useEffect(() => {
    const hs = window.history.state || {}
    if (pendingTrigger) {
      const nextInner = hs && typeof hs === 'object' && hs.state && typeof hs.state === 'object' ? { ...hs.state } : {}
      delete nextInner.cycleFaultDate
      delete nextInner.cycle_fault
      delete nextInner.cycleFault
      delete nextInner.cycle_number
      delete nextInner.cycleNumber
      delete nextInner.date
      window.history.replaceState({ ...hs, state: nextInner }, '')
    }
  }, [])

  useEffect(() => {
    if (calendarRef.current && initialTargetDate) {
      initialNavPendingRef.current = false
      const api = calendarRef.current.getApi()
      api.gotoDate(new Date(initialTargetDate))
    } else {
      initialNavPendingRef.current = false
    }
  }, [calendarRef, initialTargetDate])

  useEffect(() => {
    if (pendingTrigger && events.length > 0 && calendarRef.current) {
      const normalizeDate = (s) => {
        if (!s) return null
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.split(/[ T]/)[0]
        if (/^\d{8}/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
        return s.split(/[ T]/)[0]
      }
      const { cycleFaultDate, cycleFault, cycleNumber } = pendingTrigger
      const match = events.find((ev) => {
        let ok = true
        if (cycleFaultDate) ok = ok && normalizeDate(ev.cycleDate) === normalizeDate(cycleFaultDate)
        if (cycleFault !== undefined && cycleFault !== null && cycleFault !== '') ok = ok && ev.cycle_fault === cycleFault
        if (cycleNumber !== undefined && cycleNumber !== null && !Number.isNaN(Number(cycleNumber))) ok = ok && Number(ev.cycle_number) === Number(cycleNumber)
        return ok
      })
      if (match) {
        const api = calendarRef.current.getApi()
        const event = api.getEventById(match.id)
        if (event) {
          handleEventClick({ event })
          setPendingTrigger(null)
        }
      }
    }
  }, [events, pendingTrigger])
  useEffect(() => {
    const forceUpdateSize = () => {
      if (calendarRef.current) {
        calendarRef.current.getApi().updateSize()
      }
    }

    if (calendarWrapperRef.current) {
      const observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
            forceUpdateSize()
          }
        }
      })

      observer.observe(calendarWrapperRef.current)

      return () => {
        observer.disconnect()
      }
    }
  }, [])

  useEffect(() => {
    const handleBeforeUnload = () => {
      const currentState = window.history.state || {}
      const innerState =
        currentState && typeof currentState === 'object' && currentState.state && typeof currentState.state === 'object' ? { ...currentState.state } : {}

      const calendarViewDate =
        currentDate?.start && currentDate?.end
          ? new Date((new Date(currentDate.start).getTime() + new Date(currentDate.end).getTime()) / 2).toISOString()
          : lastCycleDate
      innerState.lastCycleDate = calendarViewDate
      innerState.hasOnlineAccess = hasOnlineAccess

      window.history.replaceState({ ...currentState, state: innerState, lastCycleDate: calendarViewDate, hasOnlineAccess: hasOnlineAccess }, '')
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [lastCycleDate, hasOnlineAccess, currentDate])

  useEffect(() => {
    if (props.isDentist && !hasOnlineAccess) {
      return
    }

    if (!currentDate) {
      return
    }

    const startDate = new Date(currentDate.start)
    const endDate = new Date(currentDate.end)
    if (isG4Plus && showFeatureDev && isG4PlusAPIEnabled) {
      getMQTTProductCycles(startDate, endDate)
    } else {
      getClassicProductCycles(startDate, endDate)
    }
  }, [currentDate, hasOnlineAccess, isG4Plus])

  useEffect(() => {
    if (lastCycleDate && calendarRef.current) {
      const api = calendarRef.current.getApi()
      api.gotoDate(new Date(lastCycleDate))
    }
  }, [lastCycleDate])
  const goToSelectedDate = (e) => {
    const date = new Date(e)

    let calendarApi = calendarRef.current.getApi()
    calendarApi.gotoDate(date)

    document.querySelector('.calendar').style.display = 'none'
    setShowDatePicker(false)
  }

  const formatSelectedDay = (date) => {
    const day = date.substring(8, 10)
    return `${day} ${localMonths[new Date(date).getMonth()]} ${new Date(date).getFullYear()}`
  }

  const handleSelectedDay = (e) => {
    setSelectedDay(formatSelectedDay(e))

    goToSelectedDate(e)
  }

  const handleDateChange = (date) => {
    if (initialNavPendingRef.current) {
      return
    }
    setCurrentDate({
      start: date?.start,
      end: date?.end,
    })
  }

  const handleEventClick = (e) => {
    const fcPopover = document.querySelector('.fc-popover')

    if (fcPopover) {
      fcPopover.style.display = 'none'
    }

    setShowSpinner(true)

    setCycleDetailsDeviceData({
      model: props.model,
      cycleNumber: e.event.extendedProps.cycle_number,
      cycleStartTime: e.event.extendedProps.cycleDate,
      deviceSerialNumber: e.event.extendedProps.serial_number,
      cid: e.event.extendedProps.cid,
      cycleFault: e.event.extendedProps.cycle_fault,
    })

    let fetchCycleDetailsPromise

    if (showFeatureDev && isG4Plus && isG4PlusAPIEnabled) {
      fetchCycleDetailsPromise = getCycleDetails(e.event.extendedProps.recordsUuid, e.event.extendedProps.serial_number)
    } else {
      fetchCycleDetailsPromise = getProductCycleDetails(e.event.extendedProps.serial_number, e.event.extendedProps.cid, { model_id: props.modelId || '' })
    }

    fetchCycleDetailsPromise
      .then((res) => {
        setShowSpinner(false)

        setCycleDetails(res.data || res || {})

        setShowCycleDetails(true)
      })
      .catch((error) => {
        setShowSpinner(false)

        toast.error(t([`error.${error?.response?.data?.code}`, 'error.something_wrong']))

        setShowCycleDetails(true)
      })
  }

  const getClassicProductCycles = async (startDate, endDate) => {
    setShowSpinner(true)

    getProductCycles(props.sn, {
      start_date: formatDate(startDate),
      end_date: formatDate(endDate),
    })
      .then((cycles) => {
        let eventData = []
        if (cycles.data) {
          eventData = cycles.data.map((cycle) => {
            return {
              id: cycle.cid,
              title: cycle.cycle_fault ? `${t('cycle')} ${cycle.cycle_number} CF-${cycle.cycle_fault}` : `${t('cycle')} ${cycle.cycle_number}`,
              className: cycle.cycle_event ? cycle.cycle_event.toLowerCase().replace(/_/g, '-') : 'cycle-pending',
              cycleDate: cycle.start,
              ...cycle,
            }
          })
        }

        setShowSpinner(false)

        setEvents(eventData)
      })
      .catch((e) => {
        console.log('error', e.message)
        setShowSpinner(false)
      })
  }

  const getMQTTProductCycles = async (startDate, endDate) => {
    setShowSpinner(true)

    getMQTTCycles(props.sn, {
      cycleStartDate: formatDate(startDate).replace(/-/g, ''),
      cycleEndDate: formatDate(endDate).replace(/-/g, ''),
    })
      .then((cycles) => {
        let eventData = []
        if (cycles.length > 0) {
          eventData = cycles.map((cycle) => {
            return {
              id: `${cycle.cycleNumber}-${cycle.cycleFault}-${cycle.cycleStartDateTime}`,
              title: cycle.cycleFault ? `${t('cycle')} ${cycle.cycleNumber} CF-${cycle.cycleFault}` : `${t('cycle')} ${cycle.cycleNumber}`,
              className: cycle.cycleEvent ? cycle.cycleEvent?.toLowerCase().replace(/_/g, '-') : 'cycle-pending',
              start: convertToSimpleDateWithFullTime(cycle.cycleStartDateTime),
              end: convertToSimpleDateWithFullTime(cycle.cycleEndDateTime),
              cycleDate: cycle.cycleStartDateTime,
              serial_number: cycle.deviceSerialNumber,
              cycle_number: cycle.cycleNumber,
              cycle_fault: cycle.cycleFault,
              recordsUuid: cycle.recordsUuid,
              cycle_start_time: cycle.cycle_start_time,
            }
          })
        }

        setShowSpinner(false)

        setEvents(eventData)
      })
      .catch((e) => {
        console.log('error', e.message)
        setShowSpinner(false)
      })
  }

  function renderEventContent(eventInfo) {
    if (windowSize > 1240) {
      return (
        <>
          <span>{eventInfo.event.title}</span>
          <span>{eventInfo.event._def.extendedProps.cycle_start_time ? eventInfo.timeText : <span> </span>}</span>
        </>
      )
    }

    if (windowSize > 360) {
      return <span>{eventInfo.event.title.split(' ', 2).toString().replaceAll(',', ' ')}</span>
    }

    return <span>{eventInfo.event.title.split(' ', 2).toString().replace('Cycle', 'Cyc').replaceAll(',', ' ')}</span>
  }

  const renderMoreLinkContent = (eventInfo) => {
    return (
      <>
        <span>{eventInfo.shortText}</span>
        <span> {t('more')}</span>
      </>
    )
  }

  const syncBravoCycles = async () => {
    const queryParams = { sn: props.sn }
    setShowSpinner(true)

    try {
      await syncBravoCyclesApi(queryParams)
      toast.success(t('device_context.sync_initiated'))
    } catch (error) {
      toast.error(t([`error.${error?.response?.data?.code}`, 'error.something_wrong']))
    } finally {
      setShowSpinner(false)
    }
  }

  return (
    <div className="cycle-calendar-wrapper" ref={calendarWrapperRef}>
      <div className={isBravoProduct ? 'legend-with-button' : ''}>
        <div className="calendar-legend">
          <StatusLegend statusData={legendData} />
        </div>
        {isBravoProduct && (!props.isDentist || (props.isDentist && hasOnlineAccess)) && (
          <SimpleButton className="action-button" onClick={() => syncBravoCycles()} disabled={showSpinner}>
            {t('device_context.sync_cycles')}
          </SimpleButton>
        )}
      </div>

      <div className="bg-white" style={{ flexGrow: 1 }}>
        <FullCalendar
          ref={calendarRef}
          locale={langCode}
          plugins={[dayGridPlugin, timeGridPlugin]}
          eventDisplay={eventDisp}
          customButtons={{
            selectDate: {
              text: t('date'),
              click: (e) => {
                const curNode = document.querySelector('.fc-selectDate-button')

                if (curNode.querySelectorAll('.calendar').length === 0) {
                  const elem = document.querySelector('.calendar')

                  curNode.parentNode.insertBefore(elem, curNode.nextSibling)
                }

                document.querySelector('.calendar').style.display = showDatePicker ? 'none' : 'block'
                setShowDatePicker(!showDatePicker)
              },
            },
          }}
          headerToolbar={{
            left: 'today,selectDate',
            center: 'prev title next',
            right: 'dayGridMonth,timeGridWeek',
          }}
          buttonText={{
            today: t('today'),
            month: t('month'),
            week: t('calendar.week'),
            day: t('calendar.day'),
          }}
          initialView="dayGridMonth"
          dayMaxEvents={true}
          eventContent={renderEventContent}
          moreLinkContent={renderMoreLinkContent}
          views={{
            dayGrid: {
              dayMaxEvents: 3,
            },
          }}
          events={events}
          eventOrderStrict={true}
          eventOrder="cycle_number"
          datesSet={handleDateChange}
          eventClick={handleEventClick}
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short',
          }}
          allDaySlot={false}
        />
        <div className={'calendar-wrapper' + (!showDatePicker ? ' visually-hidden' : '')}>
          <Calendar onChangeDay={handleSelectedDay} onChangeMonth={() => {}} selectedDay={selectedDay} />
        </div>
      </div>
      {showCycleDetails && (
        <CycleDetailsModal
          deviceData={cycleDetailsDeviceData}
          cycleDetails={cycleDetails}
          closeModal={() => {
            setShowCycleDetails(false)
            setCycleDetails({})
            setCycleDetailsDeviceData({})
          }}
          hasCycleNavigation
          isG4Plus={isG4Plus}
        />
      )}
      {showSpinner && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

export default CycleCalendar

